import { purchasingAPI, ContractFormOptions, ContractOwnerOption } from '../lib/api';
import { apiErrorMessage } from '../lib/api/errors';
import { useFetchData } from './useFetchData';

interface UseContractFormOptionsArgs {
  isOpen: boolean;
  supplierId: number;
  /** The customer the contract is with; owners load once it is set. */
  customerId: number | null;
  /** The edit form never changes the customer, so it skips that list. */
  withCustomers?: boolean;
}

export interface ContractFormOptionsState {
  customers: ContractFormOptions['customers'];
  defaultCustomer: number | null;
  customersLoading: boolean;
  customersError: string | null;
  owners: ContractOwnerOption[];
  ownersLoading: boolean;
  ownersError: string | null;
}

/**
 * CR-7787: the Customer and Contract Owner choices for the supplier contract
 * forms, from the endpoint the save itself is checked against, so neither form
 * can offer a choice the save would refuse. A failed load is reported, never
 * shown as an empty list.
 *
 * @example
 * const { customers, owners, ownersError } = useContractFormOptions({
 *   isOpen, supplierId, customerId: form.customer ? Number(form.customer) : null,
 * });
 *
 * @param args - Whether the form is open, the supplier, the chosen customer
 * @returns Both lists with their loading and error states
 */
export function useContractFormOptions({
  isOpen,
  supplierId,
  customerId,
  withCustomers = true,
}: UseContractFormOptionsArgs): ContractFormOptionsState {
  const base = useFetchData<ContractFormOptions | null>(
    async () => {
      if (!isOpen || !withCustomers) return null;
      try {
        return (await purchasingAPI.getContractFormOptions()).data;
      } catch (err: unknown) {
        throw new Error(apiErrorMessage(err, 'Customers could not be loaded.'));
      }
    },
    { deps: [isOpen, withCustomers] },
  );

  const owners = useFetchData<{ customerId: number; owners: ContractOwnerOption[] } | null>(
    async () => {
      if (!isOpen || !customerId) return null;
      try {
        const res = await purchasingAPI.getContractFormOptions({
          customer: customerId, supplier: supplierId,
        });
        return { customerId, owners: res.data.owners };
      } catch (err: unknown) {
        throw new Error(apiErrorMessage(err, 'Contract owners could not be loaded.'));
      }
    },
    { deps: [isOpen, customerId, supplierId] },
  );

  // A slow answer for a customer the user has since moved off is not shown.
  const ownersForCustomer = owners.data && owners.data.customerId === customerId
    ? owners.data.owners
    : [];

  return {
    customers: base.data?.customers ?? [],
    defaultCustomer: base.data?.default_customer ?? null,
    customersLoading: withCustomers && base.loading,
    customersError: base.error,
    owners: ownersForCustomer,
    ownersLoading: Boolean(customerId) && owners.loading,
    ownersError: owners.error,
  };
}
